<template>
  <VueFinalModal
    :esc-to-close="true"
    class="flex justify-center items-start pt-16"
    content-class="relative flex flex-col w-[1200px] max-h-[90vh] mx-auto bg-white border rounded-lg"
  >
    <div class="flex flex-col h-full">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200">
        <div>
          <h3 class="text-xl font-semibold text-gray-900">
            Find Matching Training Center Roles for {{ currentPersonName }}
          </h3>
        </div>
        <button 
          @click.stop="closeModal()"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Search Context Header -->
      <div class="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div class="flex items-center justify-between">
          <div class="flex-1">
            <h3 class="text-sm font-medium text-gray-900">
              Finding matches for: <span class="font-semibold">{{ currentPersonName }}</span>
            </h3>
            <div v-if="searchContext.paulSearchTerm" class="mt-1 text-sm text-gray-600">
              Trainer search: <span class="font-medium">{{ searchContext.paulSearchTerm }}</span>
            </div>
            <div v-else-if="searchContext.usingCreationNotes" class="mt-1 text-sm text-gray-600">
              Using creation_notes for Trainer search
            </div>
          </div>
          <button
            @click="toggleSearchForm"
            class="ml-4 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <svg class="w-4 h-4 mr-2" :class="{ 'rotate-180': showSearchForm }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
            {{ showSearchForm ? 'Hide' : 'Show' }} Search Criteria
          </button>
        </div>
      </div>

      <!-- Collapsible Search Form -->
      <div v-show="showSearchForm" class="p-6 border-b border-gray-200">
        <div class="space-y-4">
          <!-- Search Input -->
          <div>
            <label for="search-input" class="block text-sm font-medium text-gray-700 mb-2">
              Church Planter/Group Leader Name
            </label>
            <div class="relative">
              <input
                id="search-input"
                v-model="searchParams.search_term"
                type="text"
                placeholder="Search for people with same role or Trainers..."
                class="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                @input="debouncedSearch"
              />
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
            </div>
          </div>

          <!-- Paul Search Input -->
          <div>
            <label for="paul-search-input" class="block text-sm font-medium text-gray-700 mb-2">
              Trainer Name
            </label>
            <div class="relative">
              <input
                id="paul-search-input"
                v-model="searchParams.paul_search_term"
                type="text"
                placeholder="Enter specific Trainer name to search for..."
                class="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                @input="debouncedSearch"
              />
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                </svg>
              </div>
            </div>
            <p class="mt-1 text-sm text-gray-500">Leave empty to use creation_notes for Trainer search</p>
          </div>
        </div>
      </div>

      <!-- Results -->
      <div class="flex-1 overflow-y-auto p-6 min-h-0">
        <div v-if="loading" class="flex items-center justify-center h-32">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span class="ml-2 text-gray-600">Searching...</span>
        </div>

        <div v-else-if="searchResults.length === 0 && hasSearched" class="text-center py-8">
          <div class="text-gray-500 mb-4">
            <svg class="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.34 0-4.29-1.009-5.824-2.709M15 6.291A7.962 7.962 0 0012 5c-2.34 0-4.29 1.009-5.824 2.709"></path>
            </svg>
          </div>
          <h3 class="text-lg font-medium text-gray-900 mb-2">No Results Found</h3>
          <p class="text-gray-600">Try adjusting your search criteria or search terms.</p>
        </div>

        <div v-else-if="searchResults.length > 0" class="space-y-4">
          <div class="flex items-center justify-between mb-4">
            <h4 class="text-lg font-medium text-gray-900">
              Found {{ searchResults.length }} potential match{{ searchResults.length !== 1 ? 'es' : '' }}
            </h4>
            <div class="text-sm text-gray-500">
              Select one result to process
            </div>
          </div>

          <div class="space-y-3">
            <div 
              v-for="personRole in searchResults" 
              :key="personRole.id"
              class="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
              @click="selectPersonRole(personRole.id)"
            >
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <div class="flex items-center space-x-3 mb-2">
                    <input
                      type="radio"
                      :id="`person-role-${personRole.id}`"
                      :value="personRole.id"
                      v-model="selectedPersonRole"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                      @click.stop
                    />
                    <label :for="`person-role-${personRole.id}`" class="font-medium text-gray-900 cursor-pointer">
                      {{ personRole.person_name }}
                    </label>
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          :class="getRoleBadgeClass(personRole.role)">
                      {{ getRoleDisplayName(personRole.role) }}
                    </span>
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          :class="getStateBadgeClass(personRole.state)">
                      {{ personRole.state.titleize }}
                    </span>
                  </div>
                  
                  <div class="text-sm text-gray-600 space-y-1">
                    <div v-if="personRole.country_name">
                      <span class="font-medium">Country:</span> {{ personRole.country_name }}
                    </div>
                    <div v-if="personRole.creation_notes">
                      <span class="font-medium">Creation Notes:</span> 
                      <span class="italic">{{ personRole.creation_notes }}</span>
                    </div>
                    <div v-if="personRole.training_center_name">
                      <span class="font-medium">Training Center:</span> {{ personRole.training_center_name }}
                    </div>
                    <div v-if="personRole.effective_date">
                      <span class="font-medium">Effective Date:</span> {{ formatDate(personRole.effective_date) }}
                    </div>
                    <div v-if="personRole.match_type === 'duplicate_person_role'">
                      <span class="font-medium">Role:</span> {{ getRoleDisplayName(personRole.role) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="text-center py-8 text-gray-500">
          <p>Searching for people with same role and Pauls that match {{ currentPersonName }}...</p>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between p-6 border-t border-gray-200">
        <div class="text-sm text-gray-600">
          <span v-if="hasSelection">
            1 selected
          </span>
        </div>
        <div class="flex space-x-3">
          <button 
            @click="closeModal()"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button 
            @click="processSelected"
            :disabled="!hasSelection || loading"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <svg v-if="loading" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ loading ? 'Processing...' : 'Process Selected' }}
          </button>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup>
import { VueFinalModal } from 'vue-final-modal'
import { ref, onMounted, watch, computed } from 'vue'
import { debounce } from 'lodash-es'

const props = defineProps({
  params: Object,
})

const emit = defineEmits(['close'])

// Reactive data
const loading = ref(false)
const hasSearched = ref(false)
const searchResults = ref([])
const selectedPersonRole = ref(null)
const currentPersonName = ref('')
const showSearchForm = ref(false)

const searchParams = ref({
  search_term: '',
  paul_search_term: ''
})

// Computed search context for display
const searchContext = computed(() => {
  return {
    paulSearchTerm: searchParams.value.paul_search_term,
    usingCreationNotes: !searchParams.value.paul_search_term && props.params && props.params.personRole && props.params.personRole.creation_notes
  }
})

// Computed properties
const hasSelection = computed(() => {
  return selectedPersonRole.value !== null
})

// Debounced search function
const debouncedSearch = debounce(() => {
  searchPersonRoles()
}, 300)

// Methods
function closeModal() {
  emit('close')
}

function toggleSearchForm() {
  showSearchForm.value = !showSearchForm.value
}

function selectPersonRole(personRoleId) {
  selectedPersonRole.value = personRoleId
  
  // Debug output
  const selectedResult = searchResults.value.find(pr => pr.id === personRoleId)
  if (selectedResult) {
    console.log('Selected person role:', {
      id: selectedResult.id,
      person_name: selectedResult.person_name,
      role: selectedResult.role,
      relatable_type: selectedResult.relatable_type,
      relatable_id: selectedResult.relatable_id,
      state: selectedResult.state,
      training_center_name: selectedResult.training_center_name
    })
  }
}

function getRoleBadgeClass(role) {
  const classes = {
    'timothy': 'bg-blue-100 text-blue-800',
    'titus': 'bg-green-100 text-green-800',
    'paul': 'bg-purple-100 text-purple-800',
    'training_center': 'bg-orange-100 text-orange-800',
    'person': 'bg-indigo-100 text-indigo-800'
  }
  return classes[role] || 'bg-gray-100 text-gray-800'
}

function getRoleDisplayName(role) {
  const displayNames = {
    'timothy': 'Church Planter',
    'titus': 'Group Leader',
    'other_disciple': 'Other Group Leader',
    'paul': 'Trainer',
    'training_center': 'Training Center',
    'person': 'Person'
  }
  return displayNames[role] || role.titleize
}


function getStateBadgeClass(state) {
  const classes = {
    'active': 'bg-green-100 text-black',
    'inactive': 'bg-gray-100 text-gray-800',
    'dropped': 'bg-red-100 text-red-800',
    'graduated': 'bg-blue-100 text-blue-800',
    'under_review': 'bg-yellow-100 text-yellow-800',
    'suspended': 'bg-orange-100 text-orange-800',
    'paused': 'bg-gray-100 text-gray-800'
  }
  return classes[state] || 'bg-gray-100 text-gray-800'
}

function formatDate(dateString) {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString()
}



async function searchPersonRoles() {
  if (!searchParams.value.search_term && !searchParams.value.paul_search_term) {
    searchResults.value = []
    hasSearched.value = false
    selectedPersonRole.value = null
    return
  }

  loading.value = true
  hasSearched.value = true
  selectedPersonRole.value = null

  try {
    const params = new URLSearchParams()
    if (searchParams.value.search_term) params.append('search_term', searchParams.value.search_term)
    if (searchParams.value.paul_search_term) params.append('paul_search_term', searchParams.value.paul_search_term)
    if (props.params && props.params.personRole && props.params.personRole.id) params.append('person_role_id', props.params.personRole.id)

    const response = await fetch('/person_roles/find_missing_relatable?' + params.toString())
    if (response.ok) {
      const data = await response.json()
      console.log('Search results:', data)
      searchResults.value = data.person_roles || []
    } else {
      console.error('Error searching person roles:', response.statusText)
      searchResults.value = []
    }
  } catch (error) {
    console.error('Error searching person roles:', error)
    searchResults.value = []
  } finally {
    loading.value = false
  }
}

async function processSelected() {
  if (selectedPersonRole.value === null) return

  // Find the selected person role result
  const selectedResult = searchResults.value.find(pr => 
    pr.id === selectedPersonRole.value
  )

  if (!selectedResult) {
    console.error('Selected person role not found')
    return
  }

  // Get the current person role ID from params
  const currentPersonRoleId = props.params && props.params.personRole && props.params.personRole.id
  
  if (!currentPersonRoleId) {
    console.error('Current person role ID not found')
    return
  }

  try {
    loading.value = true
    
    // Get CSRF token
    const csrfTokenElement = document.querySelector('meta[name="csrf-token"]')
    const csrfToken = csrfTokenElement && csrfTokenElement.getAttribute('content')
    
    // Check if selected role is a Paul role
    if (selectedResult.role === 'paul') {
      // For Paul roles, just update the target role with relatable information
      const relatableId = selectedResult.relatable_id || null
      const relatableType = selectedResult.relatable_type || null
      
      const updateData = {
        person_role: {
          relatable_id: relatableId,
          relatable_type: relatableType
        }
      }
      
      // Call the update endpoint
      const response = await fetch(`/person_roles/${currentPersonRoleId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(updateData)
      })

      if (response.ok) {
        const result = await response.json()
        console.log('Update successful:', result)
        
        // Call the result callback if provided
        if (props.params && props.params.resultCb) {
          props.params.resultCb([selectedResult])
        }
        
        closeModal()
      } else {
        const errorText = await response.text()
        console.error('Update failed:', errorText)
        alert('Failed to update person role: ' + response.statusText)
      }
    } else {
      // For non-Paul roles, perform the merge process
      // Prepare the merge request
      // currentPersonRoleId is the target (the one we're keeping)
      // selectedResult.id is the source (the one being merged into the target)
      
      // Get relatable_id: selected result's relatable_id
      const relatableId = selectedResult.relatable_id || null
      
      // Get relatable_type: selected result's relatable_type
      const relatableType = selectedResult.relatable_type || null
      
      const mergeData = {
        bulk_action: {
          model: 'PersonRole',
          ids: [currentPersonRoleId, selectedResult.id],
          details: {
            target_id: currentPersonRoleId,
            relatable_id: relatableId,
            relatable_type: relatableType
          }
        }
      }
      
      // Call the merge endpoint
      const response = await fetch('/bulk_actions/merge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(mergeData)
      })

      if (response.ok) {
        const result = await response.json()
        console.log('Merge successful:', result)
        
        // Call the result callback if provided
        if (props.params && props.params.resultCb) {
          props.params.resultCb([selectedResult])
        }
        
        closeModal()
      } else {
        const errorText = await response.text()
        console.error('Merge failed:', errorText)
        alert('Failed to merge person roles: ' + response.statusText)
      }
    }
  } catch (error) {
    console.error('Error processing:', error)
    alert('Error processing: ' + error.message)
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(() => {
      // Initialize with person role data
      if (props.params?.personRole) {
        currentPersonName.value = props.params.personRole.person_name || 'Unknown Person'
        searchParams.value.search_term = props.params.personRole.person_name || ''
        
        // Auto-search with the person's name
        searchPersonRoles()
      }
  
  // Initialize with any provided search parameters
  if (props.params?.initialSearch) {
    searchParams.value = { ...searchParams.value, ...props.params.initialSearch }
    if (!props.params?.personRole) {
      searchPersonRoles()
    }
  }
})

// Watch for changes in search parameters
watch(searchParams, () => {
  selectedPersonRole.value = null
}, { deep: true })
</script>

<style scoped>
/* Custom scrollbar for results area */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
